import * as THREE from "three";
import type { Jet } from "../sim/backpack";

// The power washer's water (#151), render only: while the sim says it's
// spraying (`Skater.pack.jet`), a stream of droplets runs from the nozzle to
// where the jet hits around a thin core, fanning out a little on the way,
// and a mist of spray bounces off the surface there. The core is one mesh
// stretched along the jet and the droplets two instanced meshes over fixed
// pools, so spraying never allocates. Plain unlit water in every art style.

/** Droplets in the stream, and in the splash where it hits. */
const STREAM = 56;
const MIST = 40;
/** How fast the stream's droplets run along it (m/s), and how far it fans out by the end (m). */
const STREAM_SPEED = 14;
const FAN = 0.12;
/** How long a splash droplet lives (s), how fast it flies off (m/s), and the gravity that brings it down (m/s²). */
const MIST_LIFE = 0.45;
const MIST_SPEED = 2.4;
const MIST_G = 6;
const DROP_RADIUS = 0.04;
/** The jet's core: its radius (m) at the nozzle; it widens to twice that by the end. */
const CORE_RADIUS = 0.02;

/** A fixed pseudo-random 0..1 per index and channel, so the spray looks random but costs nothing. */
const rand = (i: number, k: number): number => {
  const s = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
  return s - Math.floor(s);
};

export class WashFx {
  readonly root = new THREE.Group();
  private core: THREE.Mesh;
  private stream: THREE.InstancedMesh;
  private mist: THREE.InstancedMesh;
  private t = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private p = new THREE.Vector3();
  private s = new THREE.Vector3();
  private dir = new THREE.Vector3();
  private side = new THREE.Vector3();
  private up = new THREE.Vector3();
  private n = new THREE.Vector3();
  private u = new THREE.Vector3();
  private v = new THREE.Vector3();

  constructor() {
    this.root.name = "wash";
    const mat = new THREE.MeshBasicMaterial({ color: 0xe6f7ff, transparent: true, opacity: 0.85, depthWrite: false });
    const geo = new THREE.SphereGeometry(DROP_RADIUS, 8, 6);
    // A unit-long tube along +y from 0 to 1, widening toward the end.
    const coreGeo = new THREE.CylinderGeometry(2 * CORE_RADIUS, CORE_RADIUS, 1, 8, 1, true);
    coreGeo.translate(0, 0.5, 0);
    this.core = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({ color: 0xcdeeff, transparent: true, opacity: 0.6, depthWrite: false, side: THREE.DoubleSide }));
    this.core.visible = false;
    this.core.frustumCulled = false;
    this.stream = new THREE.InstancedMesh(geo, mat, STREAM);
    this.mist = new THREE.InstancedMesh(geo, mat, MIST);
    for (const mesh of [this.stream, this.mist]) {
      mesh.count = 0;
      mesh.frustumCulled = false;
    }
    this.root.add(this.core, this.stream, this.mist);
  }

  /** Draws the jet this frame (`dt` seconds on), or nothing when it's off. */
  update(jet: Jet | null, dt: number): void {
    if (!jet) {
      this.core.visible = false;
      this.stream.count = 0;
      this.mist.count = 0;
      return;
    }
    this.t += dt;
    const len = Math.hypot(jet.x1 - jet.x0, jet.y1 - jet.y0, jet.z1 - jet.z0) || 1;
    this.dir.set(jet.x1 - jet.x0, jet.y1 - jet.y0, jet.z1 - jet.z0).divideScalar(len);
    // Two directions across the jet, to fan the droplets out in.
    this.side.set(-this.dir.z, 0, this.dir.x).normalize();
    this.up.crossVectors(this.side, this.dir);
    this.core.visible = true;
    this.core.position.set(jet.x0, jet.y0, jet.z0);
    this.core.quaternion.setFromUnitVectors(UP, this.dir);
    this.core.scale.set(1, len, 1);
    const run = (this.t * STREAM_SPEED) / len;
    for (let i = 0; i < STREAM; i++) {
      const f = (i / STREAM + run) % 1;
      const a = rand(i, 1) * Math.PI * 2;
      const r = FAN * f * rand(i, 2);
      this.p
        .set(jet.x0, jet.y0, jet.z0)
        .addScaledVector(this.dir, f * len)
        .addScaledVector(this.side, Math.cos(a) * r)
        .addScaledVector(this.up, Math.sin(a) * r);
      const size = 0.7 + 0.6 * f;
      this.stream.setMatrixAt(i, this.m.compose(this.p, this.q, this.s.set(size, size, size)));
    }
    this.stream.count = STREAM;
    this.stream.instanceMatrix.needsUpdate = true;

    const hit = jet.hit;
    if (!hit) {
      this.mist.count = 0;
      return;
    }
    // The splash: droplets flung off the surface, each on its own clock.
    this.n.set(hit.nx, hit.ny, hit.nz);
    this.u.set(this.n.y, -this.n.x, 0);
    if (this.u.lengthSq() < 1e-4) this.u.set(1, 0, 0);
    this.u.normalize();
    this.v.crossVectors(this.n, this.u);
    for (let i = 0; i < MIST; i++) {
      const age = (this.t / MIST_LIFE + rand(i, 3)) % 1;
      const time = age * MIST_LIFE;
      const a = rand(i, 4) * Math.PI * 2;
      const out = MIST_SPEED * (0.4 + 0.6 * rand(i, 5));
      const spread = 0.8 + 0.6 * rand(i, 6);
      this.p
        .set(hit.x, hit.y, hit.z)
        .addScaledVector(this.n, out * time + 0.02)
        .addScaledVector(this.u, Math.cos(a) * spread * out * time)
        .addScaledVector(this.v, Math.sin(a) * spread * out * time);
      this.p.y -= 0.5 * MIST_G * time * time;
      const size = 1.1 * (1 - age);
      this.mist.setMatrixAt(i, this.m.compose(this.p, this.q, this.s.set(size, size, size)));
    }
    this.mist.count = MIST;
    this.mist.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.core.geometry.dispose();
    (this.core.material as THREE.Material).dispose();
    this.stream.geometry.dispose();
    (this.stream.material as THREE.Material).dispose();
    this.stream.dispose();
    this.mist.dispose();
  }
}

const UP = new THREE.Vector3(0, 1, 0);
